// src/pages/PrivacyPage.js
import React from 'react';
import '../styles/PrivacyPage.css';

function PrivacyPage() {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last Updated: January 2025</p>

        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Tact Transport ("we," "our," or "us") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you use
            our services or visit our website.
          </p>
          <p>
            By using our services, you consent to the data practices described in this policy. If you
            do not agree with the terms of this policy, please do not access our services.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          <h3>Personal Information</h3>
          <p>We may collect personally identifiable information that you voluntarily provide, including:</p>
          <ul>
            <li>Name and contact information (email, phone number, address)</li>
            <li>Payment and billing information</li>
            <li>Pickup and drop-off locations</li>
            <li>Travel preferences and special requests</li>
            <li>Corporate account information (for business clients)</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <p>When you visit our website, we may automatically collect:</p>
          <ul>
            <li>Device information (browser type, operating system)</li>
            <li>IP address and general location data</li>
            <li>Website usage data and browsing patterns</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Process and fulfill your transportation reservations</li>
            <li>Communicate with you about your bookings and services</li>
            <li>Process payments and maintain billing records</li>
            <li>Improve our services and customer experience</li>
            <li>Send promotional communications (with your consent)</li>
            <li>Respond to your inquiries and support requests</li>
            <li>Comply with legal obligations</li>
            <li>Detect and prevent fraud</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Information Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share
            your information in the following circumstances:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our business (payment processors, email services)</li>
            <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets</li>
            <li><strong>Safety and Security:</strong> To protect the rights, property, or safety of Tact Transport, our clients, or others</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>
            Our website uses cookies and similar tracking technologies to enhance your browsing experience.
            Cookies are small data files stored on your device that help us:
          </p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Understand how you use our website</li>
            <li>Improve website functionality and performance</li>
            <li>Provide personalized content</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Note that disabling cookies may affect
            the functionality of our website.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction. These
            measures include:
          </p>
          <ul>
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Secure payment processing through trusted providers</li>
            <li>Regular security assessments and updates</li>
            <li>Employee training on data protection practices</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure.
            While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes outlined
            in this Privacy Policy, unless a longer retention period is required by law. Typically:
          </p>
          <ul>
            <li>Booking records: 7 years for tax and legal compliance</li>
            <li>Marketing preferences: Until you opt out</li>
            <li>Website analytics: 26 months</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>8. Your Rights</h2>
          <p>Depending on your location, you may have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service</li>
          </ul>
          <p>To exercise any of these rights, please contact us using the information below.</p>
        </section>

        <section className="privacy-section">
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under the age of 18. We do not knowingly collect
            personal information from children. If you believe we have inadvertently collected information
            from a minor, please contact us immediately.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the privacy
            practices or content of these external sites. We encourage you to review the privacy policies
            of any third-party sites you visit.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The updated version will be indicated by
            the "Last Updated" date at the top of this page. We encourage you to review this policy
            periodically for any changes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
          <ul>
            <li>Email: ride@tacttransport.com</li>
            <li>Phone: (214) 984-2522</li>
            <li>Address: PO BOX 190334, Dallas, TX 75219</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPage;
